import { Observable } from "rxjs/Observable"

import {
    SOURCE_LIST_REQUEST,
    SOURCE_LIST_SEARCH_REQUEST,
    SOURCE_APPLY_FILTER_CRITERIA,
    SOURCE_ITEM_REQUEST,
    SOURCE_LIST_RESPONSE,
    SOURCE_ITEM_RESPONSE,
    SOURCE_ITEM_CACHED_RESPONSE,
    SOURCE_CANCEL_MODEL_UPDATE,
    SOURCE_ITEM_SAVE_REQUEST,
    SOURCE_ITEM_SAVE_RESPONSE,
    SOURCE_ITEM_DELETE_REQUEST,
    SOURCE_ITEM_DELETE_RESPONSE,
    SOURCE_APPLY_MODEL_UPDATE,
    SOURCE_UPDATE_MODEL,
    SOURCE_SET_EDIT_MODE,
    SOURCE_ADD_NEW,
    SOURCE_RENDERED_LISTING_TEST_LINK,
    SOURCE_RENDERED_LISTING_DISCOVER_REQUEST
} from "../actions/action-types"
import {
    sourceListCachedResponse,
    sourceListResponse,
    sourceListErrorResponse,
    sourceListRequest,
    sourceItemResponse,
    sourceItemCachedResponse,
    sourceItemErrorResponse,
    sourceItemRequest,
    sourceItemSaveRequest,
    sourceItemSaveResponse,
    sourceItemSaveErrorResponse,
    sourceItemDeleteResponse,
    sourceItemDeleteErrorResponse,
    sourceRenderedListingCheckResponse,
    sourceRenderedListingCheckErrorResponse,
    sourceRenderedListingDiscoverResponse,
    sourceRenderedListingDiscoverErrorResponse
} from "../actions/source-actions"
import { unauthenticated } from "../actions/authentication-actions"
import { defaultSearchCriteria } from "../reducers/paging/source-paging-reducer"
import {
    hydrate as hydrateSource,
    normalisingFields
} from "../models/entities/source"
import {
    getSourceList,
    getSourceItem,
    putSourceItem,
    postSourceItem,
    deleteSourceItem,
    checkRenderedListing,
    discoverRenderedListing
} from "../api/source-api"
import {
    renderedListingCheckedFields,
    renderedListingSourceType
} from "../config/application-definitions"
import {
    requestListEpic,
    requestListSearchEpic,
    requestListFilterEpic,
    requestItemEpic,
    requestItemSaveEpic,
    requestItemDeleteEpic,
    requestTopItemFromListResponseEpic,
    syncIdToURLEpic
} from "./default-epics/default-entity-epics"
import { mapRequestError } from "./default-epics/epic-utils"

export const requestSourceListEpic = requestListEpic(
    SOURCE_LIST_REQUEST,
    getSourceList,
    "sources",
    sourceListResponse,
    sourceListCachedResponse,
    sourceListErrorResponse
)

export const requestSourceSearchEpic = requestListSearchEpic(
    SOURCE_LIST_SEARCH_REQUEST,
    sourceListRequest,
    "sources"
)

export const applySourceFilterCriteria = requestListFilterEpic(
    SOURCE_APPLY_FILTER_CRITERIA,
    sourceListRequest,
    "sources"
)

export const requestSourceItemEpic = requestItemEpic(
    SOURCE_ITEM_REQUEST,
    getSourceItem,
    "sources",
    hydrateSource,
    normalisingFields,
    sourceItemResponse,
    sourceItemCachedResponse,
    sourceItemErrorResponse
)

export const requestInsightAreaCancelModelUpdateEpic = (
    action$,
    { getState }
) =>
    action$
        .ofType(SOURCE_CANCEL_MODEL_UPDATE)
        .map(() => getState().presentation.sources.activeId)
        .filter(id => id)
        .map(sourceItemRequest)

export const requestCommunityApplyModelUpdateEpic = (action$, { getState }) =>
    action$.ofType(SOURCE_APPLY_MODEL_UPDATE).map(() => {
        const {
            activeId,
            updateModel,
            imageToUpload
        } = getState().presentation.sources
        let newUpdateModel = activeId
            ? updateModel.set("id", activeId)
            : updateModel
        return sourceItemSaveRequest(
            activeId,
            newUpdateModel.toJS(),
            imageToUpload
        )
    })

export const requestSourceItemSaveEpic = requestItemSaveEpic(
    SOURCE_ITEM_SAVE_REQUEST,
    putSourceItem,
    postSourceItem,
    "source",
    sourceItemSaveResponse,
    sourceItemSaveErrorResponse
)

export const requestSourceItemDeleteEpic = requestItemDeleteEpic(
    SOURCE_ITEM_DELETE_REQUEST,
    deleteSourceItem,
    "source",
    sourceItemDeleteResponse,
    sourceItemDeleteErrorResponse
)

export const responseSourceItemDeleteEpic = (action$, { getState }) =>
    action$.ofType(SOURCE_ITEM_DELETE_RESPONSE).map(action => {
        const state = getState()
        const string = state.presentation.sources.searchCriteria
        const pagingRequest = state.paging.sources[string]
        return sourceItemRequest(pagingRequest.data[0])
    })

export const responseSourceListEpic = requestTopItemFromListResponseEpic(
    SOURCE_LIST_RESPONSE,
    sourceItemRequest,
    "sources",
    "sources",
    defaultSearchCriteria
)

export const syncSourceIdToURLEpic = syncIdToURLEpic(
    "sources",
    SOURCE_ITEM_RESPONSE,
    SOURCE_ITEM_SAVE_RESPONSE,
    SOURCE_ITEM_CACHED_RESPONSE
)

// Check a Rendered listing source's settings with dashboard-api while it's edited, so the form shows the verdicts
// the save and the scraper reach. A pause in typing sends one check, and a newer edit cancels one in flight.
export const sourceRenderedListingCheckEpic = (action$, { getState }) =>
    action$
        .filter(
            ({ type, name }) =>
                [
                    SOURCE_SET_EDIT_MODE,
                    SOURCE_ADD_NEW,
                    SOURCE_RENDERED_LISTING_TEST_LINK,
                ].includes(type) ||
                (type === SOURCE_UPDATE_MODEL &&
                    renderedListingCheckedFields.includes(name))
        )
        .debounceTime(300)
        .switchMap(() => {
            const state = getState()
            const {
                workingModel,
                isEditMode,
                isCreateMode,
                renderedListingTestLink
            } = state.presentation.sources
            if (
                !(isEditMode || isCreateMode) ||
                workingModel.type !== renderedListingSourceType
            ) {
                return Observable.of()
            }
            return checkRenderedListing(state.authentication.token, {
                url: workingModel.url || "",
                link_pattern: workingModel.link_pattern || "",
                listing_urls: workingModel.listing_urls || [],
                preserve_article_links: workingModel.preserve_article_links === true,
                test_link: renderedListingTestLink || ""
            })
                .map(sourceRenderedListingCheckResponse)
                .catch(error =>
                    Observable.of(
                        mapRequestError(
                            error,
                            unauthenticated,
                            sourceRenderedListingCheckErrorResponse
                        )
                    )
                )
        })

// Look at a page for the moderator: dashboard-api renders it and groups its links, so they can pick the content.
// Each look uses a Firecrawl credit, so it's only sent when asked for.
export const sourceRenderedListingDiscoverEpic = (action$, { getState }) =>
    action$.ofType(SOURCE_RENDERED_LISTING_DISCOVER_REQUEST).switchMap(({ url }) =>
        discoverRenderedListing(getState().authentication.token, url)
            .map(discover => sourceRenderedListingDiscoverResponse(discover, url))
            .catch(error =>
                Observable.of(
                    mapRequestError(
                        error,
                        unauthenticated,
                        sourceRenderedListingDiscoverErrorResponse,
                        url
                    )
                )
            )
    )
